import { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { Spinner } from "@/components/ui";
import { cn } from "@/lib/utils";
import {
  CHAT_INPUT_BAR_CLASS,
  CHAT_SEND_BUTTON_CLASS,
  CHAT_TEXTAREA_CLASS,
} from "./theme";

export interface MessengerChatInputProps {
  onSend: (message: string) => Promise<void>;
  isSending: boolean;
  disabled?: boolean;
  placeholder?: string;
  sendLabel: string;
  loadingLabel: string;
}

export function MessengerChatInput({
  onSend,
  isSending,
  disabled = false,
  placeholder,
  sendLabel,
  loadingLabel,
}: MessengerChatInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = async () => {
    if (!message.trim()) return;
    await onSend(message.trim());
    setMessage("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [message]);

  return (
    <div className={CHAT_INPUT_BAR_CLASS}>
      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={placeholder}
        rows={1}
        disabled={disabled || isSending}
        onKeyDown={handleKeyDown}
        className={CHAT_TEXTAREA_CLASS}
      />
      <button
        type="button"
        onClick={() => void handleSend()}
        disabled={disabled || isSending || !message.trim()}
        aria-label={sendLabel}
        className={cn(
          CHAT_SEND_BUTTON_CLASS,
          "bg-primary-fill hover:bg-primary-fill-strong",
        )}
      >
        {isSending ? (
          <Spinner size="sm" variant="inverse" loadingLabel={loadingLabel} />
        ) : (
          <Send className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}
